import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import {
  listReportsAndPosts,
  suggestReportPostMatches,
  linkReportToPost,
  type MatchSuggestion,
  type ReportMini,
} from "@/lib/report-links.functions";

const searchSchema = z.object({
  q: z.string().optional(),
  action: z.enum(["fix-links"]).optional(),
});

export const Route = createFileRoute("/admin/report-links")({
  head: () => ({
    meta: [
      { title: "Rapor ↔ Yazı eşleştirme" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  validateSearch: searchSchema,
  component: ReportLinksPage,
});

function RelevancePill({ r }: { r: MatchSuggestion["relevance"] }) {
  const map: Record<string, string> = {
    high: "bg-emerald-600/10 text-emerald-700 border-emerald-600/20",
    medium: "bg-amber-500/10 text-amber-700 border-amber-500/20",
    low: "bg-ink/5 text-ink/60 border-ink/10",
  };
  const label = r === "high" ? "yüksek" : r === "medium" ? "orta" : "düşük";
  return (
    <span
      className={`inline-block text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded border ${map[r]}`}
    >
      {label}
    </span>
  );
}

function ReportLinksPage() {
  const search = Route.useSearch();
  const load = useServerFn(listReportsAndPosts);
  const suggest = useServerFn(suggestReportPostMatches);
  const link = useServerFn(linkReportToPost);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["admin", "report-links"],
    queryFn: () => load(),
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<Record<string, MatchSuggestion[]>>({});
  const [busy, setBusy] = useState<string | null>(null); // reportId that is running
  const [linking, setLinking] = useState<string | null>(null); // "reportId:postId"
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [q, setQ] = useState(search.q ?? "");
  const autoStarted = useRef(false);

  useEffect(() => {
    setQ(search.q ?? "");
  }, [search.q]);

  const reports = data?.reports ?? [];
  const posts = data?.posts ?? [];
  const postCount = posts.length;

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return reports;
    return reports.filter(
      (r) =>
        r.title.toLowerCase().includes(needle) ||
        r.organization.toLowerCase().includes(needle) ||
        r.category.toLowerCase().includes(needle),
    );
  }, [reports, q]);

  const showCop31Audit =
    search.action === "fix-links" && (search.q ?? "").toLowerCase().includes("cop31");

  const runSuggest = useMutation({
    mutationFn: async (reportId: string) => {
      setBusy(reportId);
      setErrors((e) => ({ ...e, [reportId]: "" }));
      try {
        const r = await suggest({ data: { reportId } });
        setSuggestions((s) => ({ ...s, [reportId]: r.suggestions }));
      } catch (err) {
        setErrors((e) => ({
          ...e,
          [reportId]: err instanceof Error ? err.message : "Hata",
        }));
      } finally {
        setBusy(null);
      }
    },
  });

  useEffect(() => {
    if (search.action !== "fix-links" || autoStarted.current || reports.length === 0) return;
    const target = reports.find((r) =>
      [r.title, r.organization, r.category].join(" ").toLowerCase().includes((search.q ?? "cop31").toLowerCase()),
    );
    if (!target) return;
    autoStarted.current = true;
    setSelectedId(target.id);
    runSuggest.mutate(target.id);
  }, [reports, runSuggest, search.action, search.q]);

  const runLink = async (report: ReportMini, s: MatchSuggestion) => {
    const key = `${report.id}:${s.postId}`;
    setLinking(key);
    try {
      await link({ data: { reportId: report.id, postId: s.postId } });
      // Optimistically remove from suggestions
      setSuggestions((cur) => ({
        ...cur,
        [report.id]: (cur[report.id] ?? []).filter((x) => x.postId !== s.postId),
      }));
      // Refetch to update linkedPostIds count
      refetch();
    } catch (err) {
      setErrors((e) => ({
        ...e,
        [report.id]: err instanceof Error ? err.message : "Bağlanamadı",
      }));
    } finally {
      setLinking(null);
    }
  };

  if (isLoading) {
    return <div className="text-sm text-ink/60">Yükleniyor…</div>;
  }

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-3xl">Rapor ↔ Yazı eşleştirme</h1>
          <p className="mt-2 text-sm text-ink/60 max-w-2xl leading-relaxed">
            Her rapor için AI, arşivdeki yayınlanmış {postCount} yazıyı tarayıp
            hangilerinde anlamlı biçimde referans verilebileceğini önerir.
            Tek tıkla ilgili yazının kaynakçasına ekle.
          </p>
        </div>
        <div className="flex gap-3 items-center">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rapor ara…"
            className="text-sm border border-border rounded px-3 py-1.5 bg-paper w-64"
          />
          <Link
            to="/admin/ai-health"
            className="text-xs text-ink/60 hover:text-ink underline"
          >
            ← AI Health
          </Link>
        </div>
      </header>

      {search.action === "fix-links" && (
        <div className="rounded border border-clay/30 bg-clay/5 p-4 text-sm text-ink/70">
          COP31 filtresi uygulandı. Eşleşen rapor bulunduğunda AI önerileri otomatik çalıştırılır; ardından ilgili yazıya tek tıkla kaynak bağlantısı ekleyebilirsiniz.
        </div>
      )}

      {showCop31Audit && <Cop31LinkAudit />}

      <div className="space-y-4">
        {showCop31Audit && filtered.length === 0 && (
          <div className="rounded border border-border bg-paper p-4 text-sm text-ink/70">
            COP31 statik sayfası için bağlantı denetimi yukarıda otomatik açıldı.
          </div>
        )}
        {filtered.map((r) => {
          const isBusy = busy === r.id;
          const sugg = suggestions[r.id];
          const err = errors[r.id];
          const expanded = selectedId === r.id;
          return (
            <div
              key={r.id}
              className="border border-border rounded bg-paper overflow-hidden"
            >
              <div className="flex items-start justify-between gap-4 p-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-ink/50 font-bold">
                      {r.category || "rapor"}
                    </span>
                    {r.year && (
                      <span className="text-[10px] text-ink/40">· {r.year}</span>
                    )}
                    <span className="text-[10px] text-ink/40">· {r.host}</span>
                    <span className="text-[10px] text-ink/40">
                      · {r.linkedPostIds.length} yazıda referans
                    </span>
                  </div>
                  <div className="mt-1 font-serif text-lg leading-tight">
                    {r.title}
                  </div>
                  {r.organization && (
                    <div className="text-xs text-ink/60 mt-0.5">
                      {r.organization}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => {
                      setSelectedId(expanded ? null : r.id);
                      if (!sugg && !isBusy) runSuggest.mutate(r.id);
                    }}
                    disabled={isBusy}
                    className="text-xs px-3 py-1.5 rounded bg-clay text-paper hover:bg-clay/90 disabled:opacity-50"
                  >
                    {isBusy ? "Analiz ediliyor…" : sugg ? (expanded ? "Kapat" : "Önerileri göster") : "AI ile eşleşme öner"}
                  </button>
                </div>
              </div>

              {expanded && (
                <div className="border-t border-border bg-ink/[0.02] p-4">
                  {err && (
                    <div className="text-xs text-rose-600 mb-3">{err}</div>
                  )}
                  {isBusy && (
                    <div className="text-xs text-ink/60">
                      Yazılar taranıyor, ilgili olanlar seçiliyor…
                    </div>
                  )}
                  {!isBusy && sugg && sugg.length === 0 && (
                    <div className="text-xs text-ink/60">
                      Bu rapor için uygun yazı bulunamadı ya da tüm ilgili yazılara
                      zaten bağlı.
                    </div>
                  )}
                  {!isBusy && sugg && sugg.length > 0 && (
                    <ul className="space-y-2">
                      {sugg.map((s) => {
                        const key = `${r.id}:${s.postId}`;
                        const isLinking = linking === key;
                        return (
                          <li
                            key={s.postId}
                            className="flex items-start justify-between gap-4 border border-border rounded bg-paper p-3"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <RelevancePill r={s.relevance} />
                                <Link
                                  to="/admin/$id"
                                  params={{ id: s.postId }}
                                  className="font-serif text-sm hover:underline"
                                >
                                  {s.title}
                                </Link>
                                <a
                                  href={`/yazilar/${s.slug}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-[10px] text-ink/50 hover:text-ink underline"
                                >
                                  ↗ önizle
                                </a>
                              </div>
                              {s.why && (
                                <p className="mt-1 text-xs text-ink/70 leading-relaxed">
                                  {s.why}
                                </p>
                              )}
                            </div>
                            <button
                              onClick={() => runLink(r, s)}
                              disabled={isLinking}
                              className="text-xs px-3 py-1.5 rounded border border-clay text-clay hover:bg-clay hover:text-paper transition disabled:opacity-50 shrink-0"
                            >
                              {isLinking ? "Ekleniyor…" : "Kaynakçaya ekle"}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-sm text-ink/60">Sonuç yok.</div>
        )}
      </div>

      {isFetching && (
        <div className="text-[10px] text-ink/40">Güncelleniyor…</div>
      )}
    </div>
  );
}

function Cop31LinkAudit() {
  const rows = [
    { label: "UNFCCC — COP31", url: "https://unfccc.int/cop31", status: "Sağlıklı" },
    { label: "UNFCCC — The Road to Antalya", url: "https://unfccc.int/cop31/the-road-to-antalya", status: "Sağlıklı" },
    { label: "UNFCCC — Katılımcı Bilgileri", url: "https://unfccc.int/cop31/ifp", status: "Sağlıklı" },
    { label: "COP31 Türkiye Başkanlığı", url: "https://cop31.tr", status: "DNS beklemede" },
    { label: "Mavi Bölge Pavilyon Portalı", url: "https://cop31.center", status: "Sağlıklı" },
  ];
  return (
    <section className="rounded border border-clay/30 bg-clay/5 p-4">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-serif text-xl">COP31 bağlantı denetimi</h2>
          <p className="mt-1 text-xs text-ink/65">
            Otomatik akış açıldı; erişim riski taşıyan COP31 kaynakları ayrıştırıldı.
          </p>
        </div>
        <span className="text-[10px] uppercase tracking-[0.2em] text-clay">1 aksiyon gerekli</span>
      </div>
      <ul className="mt-4 divide-y divide-border rounded border border-border bg-paper">
        {rows.map((r) => {
          const risky = r.status !== "Sağlıklı";
          return (
            <li key={r.url} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <a href={r.url} target="_blank" rel="noreferrer" className="truncate hover:text-clay underline underline-offset-2">
                {r.label}
              </a>
              <span className={`shrink-0 text-[10px] uppercase tracking-[0.16em] ${risky ? "text-clay" : "text-moss"}`}>
                {r.status}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="mt-3 text-xs text-ink/60">
        Güvenli akış: DNS aktif olana kadar resmi UNFCCC sayfaları ve cop31.center birincil kaynak olarak tutulmalı.
      </p>
    </section>
  );
}
